import { useEffect, useRef, useState } from 'react';

import type { FormEncType } from '@remix-run/react';

type UnwrapPromise<T> = T extends Promise<infer U> ? U : T;

// Accepts either a plain data type (`useNonRevalidatingFetcher<Data>()`) or
// the legacy `() => Promise<Data>` form used by older consumers.
export function useNonRevalidatingFetcher<T>() {
  type Unwrapped = T extends (args: unknown) => Promise<unknown> ? UnwrapPromise<ReturnType<T>> : T;

  const [state, setState] = useState<'idle' | 'loading' | 'submitting'>('idle');
  const [data, setData] = useState<Unwrapped | undefined>(undefined);
  // Last URL passed to load(), so the dev-only mock-change listener below can
  // re-fire the same fetch when the bee panel mutates registry state.
  const lastUrlRef = useRef<string | null>(null);

  const load = async (url: string) => {
    lastUrlRef.current = url;
    const abortController = new AbortController();
    setState('loading');
    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const result = await response.json();
      handleComponentError(result);
      setData(result);
    } catch (error) {
      console.error('Fetch error:', error);
      setData(undefined); // Reset data on error
    } finally {
      setState('idle');
    }
    return () => {
      abortController.abort(); // Cleanup on unmount
    };
  };

  const submit = async (
    body: BodyInit,
    options: RequestInit & { action?: string; encType?: FormEncType },
  ): Promise<{ ok: boolean; status: number; data: Unwrapped | undefined }> => {
    setState('submitting');
    try {
      // Set Content-Type header based on encType
      const headers = new Headers(options.headers);
      if (options.encType === 'application/json') {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(options?.action ?? '.', {
        ...options,
        body,
        headers,
      });
      const text = await response.text();
      let result: Unwrapped | undefined;
      try {
        result = text.length > 0 ? (JSON.parse(text) as Unwrapped) : undefined;
      } catch {
        result = undefined;
      }
      if (result !== undefined) {
        handleComponentError(result);
      }
      // Store response data regardless of status code, matching Remix useFetcher
      // behavior. This ensures error toasts (from toastError) flow through to
      // ActionForm's onError callback instead of being swallowed.
      setData(result);
      return { ok: response.ok, status: response.status, data: result };
    } catch (error) {
      console.error('Fetch error:', error);
      setData(undefined); // Reset data on error
      return { ok: false, status: 0, data: undefined };
    } finally {
      setState('idle');
    }
  };

  // Dev-only: re-fire the last load() when the bee panel toggles a mock.
  // `beehiiv:mockChanged` is only dispatched by app/components/DevToolsPanel,
  // so this listener is inert in prod (event never fires) and cheap to leave
  // in place — avoids gating with `import.meta.env.DEV` in a hook.
  useEffect(() => {
    const onMockChange = () => {
      if (lastUrlRef.current) void load(lastUrlRef.current);
    };
    window.addEventListener('beehiiv:mockChanged', onMockChange);
    return () => window.removeEventListener('beehiiv:mockChanged', onMockChange);
  }, []);

  return { state, data, load, submit, Form: 'form' as const };
}

function handleComponentError<T>(fetcherData: T) {
  if (fetcherData && typeof fetcherData === 'object' && '$$error' in fetcherData) {
    throw fetcherData?.$$error;
  }
}
